import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { InlineSVGModule } from "ng-inline-svg-2";
import { DestinationSettingsModule } from "app/js/src/components/destinations/destination-settings/destination-settings.module";

import { ClippingStudioRoutes } from "./clipping-studio.routes";
import { ClippingStudioRootComponent } from "./clipping-studio-root/clipping-studio-root.component";
import { ClippingStudioComponent } from "./clipping-studio.component";
import { ClippingStudioVideoPreviewComponent } from "./video-preview/clipping-studio-video-preview.component";
import { ClippingStudioTimelineComponent } from "./timeline/clipping-studio-timeline.component";
import { ClippingStudioTimelineMarkerComponent } from "./timeline/clipping-studio-timeline-marker/clipping-studio-timeline-marker.component";
import { ClippingStudioLeftSidebarComponent } from "./left-sidebar/clipping-studio-left-sidebar.component";
import { ClippingStudioRightSidebarComponent } from "./right-sidebar/clipping-studio-right-sidebar.component";
import { ClippingStudioFindClipPanelComponent } from "./panels/find-clip-panel/clipping-studio-find-clip-panel.component";
import { ClippingStudioMediaPanelComponent } from "./panels/media-panel/clipping-studio-media-panel.component";
import { ClippingStudioMamMediaCardComponent } from "./panels/media-panel/mam-media-card/clipping-studio-mam-media-card.component";
import { ClippingStudioTranscriptSubtitlesPanelComponent } from "./panels/transcript-subtitles-panel/clipping-studio-transcript-subtitles-panel.component";
import { ClippingStudioTranscriptBlockComponent } from "./panels/transcript-subtitles-panel/transcript-block/clipping-studio-transcript-block.component";
import { ClippingStudioSubtitleBlockComponent } from "./panels/transcript-subtitles-panel/subtitle-block/clipping-studio-subtitle-block.component";
import { ClippingStudioTranscriptFloatingToolbarComponent } from "./panels/transcript-subtitles-panel/floating-toolbar/clipping-studio-transcript-floating-toolbar.component";
import { ClippingStudioSubtitleStylePanelComponent } from "./panels/transcript-subtitles-panel/subtitle-style-panel/clipping-studio-subtitle-style-panel.component";
import { ClippingStudioGraphicsPanelComponent } from "./panels/graphics-panel/clipping-studio-graphics-panel.component";
import { ClippingStudioMamGraphicParamsEditorComponent } from "./panels/graphics-panel/mam-graphic-params-editor/clipping-studio-mam-graphic-params-editor.component";
import { ClippingStudioGraphicCardComponent } from "./panels/graphics-panel/graphic-card/clipping-studio-graphic-card.component";
import { ClippingStudioMamGraphicCardComponent } from "./panels/graphics-panel/mam-graphic-card/clipping-studio-mam-graphic-card.component";
import { ClippingStudioLogoEditorComponent } from "./panels/graphics-panel/logo-editor/clipping-studio-logo-editor.component";
import { ClippingStudioFruitIconEditorComponent } from "./panels/graphics-panel/fruit-icon-editor/clipping-studio-fruit-icon-editor.component";
import { ClippingStudioEmojiEditorComponent } from "./panels/graphics-panel/emoji-editor/clipping-studio-emoji-editor.component";
import { ClippingStudioTextPanelComponent } from "./panels/text-panel/clipping-studio-text-panel.component";
import { ClippingStudioTextTemplateCardComponent } from "./panels/text-panel/text-template-card/clipping-studio-text-template-card.component";
import { ClippingStudioTextStylingPanelComponent } from "./panels/text-panel/text-styling-panel/clipping-studio-text-styling-panel.component";
import { ClippingStudioDestinationTabComponent } from "./panels/destination-tab/clipping-studio-destination-tab.component";
import { ClippingStudioPublishTabComponent } from "./panels/publish-tab/clipping-studio-publish-tab.component";
import { ClippingStudioTikTokPublishSettingsComponent } from "./panels/destination-tab/tiktok-publish-settings/clipping-studio-tiktok-publish-settings.component";
import { ClippingStudioFacebookPublishSettingsComponent } from "./panels/destination-tab/facebook-publish-settings/clipping-studio-facebook-publish-settings.component";
import { ClippingStudioInstagramPublishSettingsComponent } from "./panels/destination-tab/instagram-publish-settings/clipping-studio-instagram-publish-settings.component";
import { ClippingStudioYoutubePublishSettingsComponent } from "./panels/destination-tab/youtube-publish-settings/clipping-studio-youtube-publish-settings.component";
import { ClippingStudioMetadataTabComponent } from "./panels/metadata-tab/clipping-studio-metadata-tab.component";
import { ClippingStudioThumbnailTabComponent } from "./panels/thumbnail-tab/clipping-studio-thumbnail-tab.component";
import { ClippingStudioAutoReframeModalComponent } from "./auto-reframe-modal/clipping-studio-auto-reframe-modal.component";
import { ClippingStudioCatalogCardComponent } from "./shared/catalog-card/clipping-studio-catalog-card.component";
import { ClippingStudioColorSwatchPickerComponent } from "./shared/color-swatch-picker/clipping-studio-color-swatch-picker.component";
import { ClippingStudioDestinationPickerComponent } from "./shared/destination-picker/clipping-studio-destination-picker.component";
import { ClippingStudioPublishPreviewCardComponent } from "./shared/publish-preview-card/clipping-studio-publish-preview-card.component";

import { IClippingStudioRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-repository";
import { ClippingStudioHttpRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-http-repository.service";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { ClippingStudioAiReframeService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-ai-reframe.service";
import { ClippingStudioCanvasService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-canvas.service";
import { TimelineThumbnailVttService } from "app/js/src/infrastructure/clipping-studio/timeline-thumbnail-vtt.service";
import { ClippingStudioFontService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-font.service";
import { IClippingStudioFindClipRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-find-clip-repository";
import { ClippingStudioFindClipRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-find-clip-repository.service";
import { ClippingStudioDestinationsAdapterService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-destinations-adapter.service";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { IClippingStudioTranscriptRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-transcript-repository";
import { ClippingStudioTranscriptHttpRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-transcript-http-repository.service";
import { EvrideoCommonModule } from "../common/evrideo-common.module";
import { IClippingStudioGraphicsRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-graphics-repository";
import { ClippingStudioGraphicsHttpRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-graphics-http-repository.service";
import { IClippingStudioGfxRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-gfx-repository";
import { ClippingStudioGfxHttpRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-http-repository.service";
import { ClippingStudioGfxBridgeService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-bridge.service";
import { ClippingStudioGfxManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-manager.service";
import { IClippingStudioMamCatalogService } from "app/js/src/domain/clipping-studio/i-clipping-studio-mam-catalog.service";
import { ClippingStudioMamCatalogService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-mam-catalog.service";
import { IDestinationsProvider } from "app/js/src/domain/clipping-studio/publish/i-destinations-provider";
import { IGenerateMetadataRepository } from "app/js/src/domain/clipping-studio/publish/i-generate-metadata-repository";
import { ClippingStudioGenerateMetadataRepositoryService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-generate-metadata-repository.service";
import { IDestinationTypeDefaultsProvider } from "app/js/src/domain/clipping-studio/publish/i-destination-type-defaults-provider";
import { ClippingStudioDestinationTypeDefaultsHttpProviderService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-destination-type-defaults-http-provider.service";
import { ITextTemplatesProvider } from "app/js/src/domain/clipping-studio/i-text-templates-provider";
import { ClippingStudioTextTemplatesHttpService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-text-templates-http.service";
import { IEmojiPickerProvider } from "app/js/src/domain/clipping-studio/i-emoji-picker-provider";
import { ClippingStudioEmojiPickerHttpService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-emoji-picker-http.service";
import { CreateLiveEventBroadcastFlowService } from "./flows/create-live-event-broadcast-flow.service";
import { PublishingStatusModule } from "../publishing-status/publishing-status.module";
import { MamExplorerModule } from "../mam-explorer/mam-explorer.module";

@NgModule({
  declarations: [
    ClippingStudioRootComponent,
    ClippingStudioComponent,
    ClippingStudioVideoPreviewComponent,
    ClippingStudioTimelineComponent,
    ClippingStudioTimelineMarkerComponent,
    ClippingStudioLeftSidebarComponent,
    ClippingStudioRightSidebarComponent,
    ClippingStudioFindClipPanelComponent,
    ClippingStudioMediaPanelComponent,
    ClippingStudioMamMediaCardComponent,
    ClippingStudioTranscriptSubtitlesPanelComponent,
    ClippingStudioTranscriptBlockComponent,
    ClippingStudioSubtitleBlockComponent,
    ClippingStudioTranscriptFloatingToolbarComponent,
    ClippingStudioSubtitleStylePanelComponent,
    ClippingStudioGraphicsPanelComponent,
    ClippingStudioMamGraphicParamsEditorComponent,
    ClippingStudioCatalogCardComponent,
    ClippingStudioColorSwatchPickerComponent,
    ClippingStudioDestinationPickerComponent,
    ClippingStudioPublishPreviewCardComponent,
    ClippingStudioGraphicCardComponent,
    ClippingStudioMamGraphicCardComponent,
    ClippingStudioLogoEditorComponent,
    ClippingStudioFruitIconEditorComponent,
    ClippingStudioEmojiEditorComponent,
    ClippingStudioTextPanelComponent,
    ClippingStudioTextTemplateCardComponent,
    ClippingStudioTextStylingPanelComponent,
    ClippingStudioDestinationTabComponent,
    ClippingStudioPublishTabComponent,
    ClippingStudioTikTokPublishSettingsComponent,
    ClippingStudioFacebookPublishSettingsComponent,
    ClippingStudioInstagramPublishSettingsComponent,
    ClippingStudioYoutubePublishSettingsComponent,
    ClippingStudioMetadataTabComponent,
    ClippingStudioThumbnailTabComponent,
    ClippingStudioAutoReframeModalComponent,
  ],
  imports: [
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    PublishingStatusModule,
    MamExplorerModule,
    DestinationSettingsModule,
    RouterModule.forChild(ClippingStudioRoutes),
    DragDropModule,
    ScrollingModule,
    InlineSVGModule,
    EvrideoCommonModule,
  ],
  providers: [
    {
      provide: IClippingStudioRepository,
      useClass: ClippingStudioHttpRepositoryService,
    },
    {
      provide: IClippingStudioFindClipRepository,
      useClass: ClippingStudioFindClipRepositoryService,
    },
    {
      provide: IClippingStudioTranscriptRepository,
      useClass: ClippingStudioTranscriptHttpRepositoryService,
    },
    {
      provide: IClippingStudioGraphicsRepository,
      useClass: ClippingStudioGraphicsHttpRepositoryService,
    },
    {
      provide: IClippingStudioMamCatalogService,
      useClass: ClippingStudioMamCatalogService,
    },
    {
      provide: IDestinationsProvider,
      useClass: ClippingStudioDestinationsAdapterService,
    },
    {
      provide: IGenerateMetadataRepository,
      useClass: ClippingStudioGenerateMetadataRepositoryService,
    },
    {
      provide: IDestinationTypeDefaultsProvider,
      useClass: ClippingStudioDestinationTypeDefaultsHttpProviderService,
    },
    {
      provide: ITextTemplatesProvider,
      useClass: ClippingStudioTextTemplatesHttpService,
    },
    {
      provide: IEmojiPickerProvider,
      useClass: ClippingStudioEmojiPickerHttpService,
    },
    {
      provide: IClippingStudioGfxRepository,
      useClass: ClippingStudioGfxHttpRepositoryService,
    },
    ClippingStudioManagerService,
    ClippingStudioAiReframeService,
    ClippingStudioCanvasService,
    ClippingStudioGfxBridgeService,
    ClippingStudioGfxManagerService,
    TimelineThumbnailVttService,
    ClippingStudioFontService,
    PublishStateService,
    CreateLiveEventBroadcastFlowService,
  ],
})
export class ClippingStudioModule {}
